import React from 'react';
import { useTheme } from '@mui/material/styles';
import type { ThemedSvgProps } from '../../types';

/**
 * Creates a four-pointed star path centered at (cx, cy) with the given radius.
 * The star has 4 points radiating outward (like a sparkle/twinkle).
 */
function fourPointStar(cx: number, cy: number, outerR: number): string {
  const innerR = outerR * 0.35;
  return [
    `M${cx},${cy - outerR}`,
    `L${cx + innerR},${cy - innerR}`,
    `L${cx + outerR},${cy}`,
    `L${cx + innerR},${cy + innerR}`,
    `L${cx},${cy + outerR}`,
    `L${cx - innerR},${cy + innerR}`,
    `L${cx - outerR},${cy}`,
    `L${cx - innerR},${cy - innerR}`,
    'Z',
  ].join(' ');
}

const StarsBase: React.FC<ThemedSvgProps> = ({ color, size = 60, className }) => {
  const theme = useTheme();
  const fill = color || theme.palette.venue.kids;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="presentation"
      className={className}
    >
      {/* Star 1 - large */}
      <path d={fourPointStar(25, 25, 10)} fill={fill} opacity={1.0} />

      {/* Star 2 - medium */}
      <path d={fourPointStar(70, 18, 8)} fill={fill} opacity={0.7} />

      {/* Star 3 - small */}
      <path d={fourPointStar(50, 55, 6)} fill={fill} opacity={0.5} />

      {/* Star 4 - large */}
      <path d={fourPointStar(80, 65, 9)} fill={fill} opacity={0.8} />

      {/* Star 5 - tiny */}
      <path d={fourPointStar(20, 78, 5)} fill={fill} opacity={0.4} />
    </svg>
  );
};

export const Stars = React.memo(StarsBase);
